<template>
  <div>
    <a-modal
      v-model="visible"
      title="Invoice Factoring Request Decision"
      @ok="handleOk"
      width="50%"
      :closable="true"
      :maskClosable="true"
      @cancel="handleCancel"
      ok-text="Confirm & Disburse"
      :ok-type="'primary'"
      :ok-button-props="{
        style: {
          backgroundColor: '#26a246',
          borderColor: '#26a246',
          borderRadius: '6px',
          fontWeight: '600',
        },
      }"
    >
      <a-breadcrumb style="margin-bottom: 16px">
        <a-breadcrumb-item>Payments & Financing</a-breadcrumb-item>
        <a-breadcrumb-item
          ><router-link
            to="/seller-management"
            style="font-weight: bold; font-size: 14px"
          >
            Factoring Request view</router-link
          ></a-breadcrumb-item
        >
        <a-breadcrumb-item>Request #INV-2025-001</a-breadcrumb-item>
      </a-breadcrumb>

      <a-alert
        v-if="
          requestData &&
          requestData.buyerInfo &&
          requestData.buyerInfo.riskLevel === 'High Risk'
        "
        message="High Risk Score Detected"
        description="This request has a high risk score and the buyer has 2 overdue invoices."
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      />

      <a-alert
        v-else-if="
          requestData &&
          requestData.buyerInfo &&
          requestData.buyerInfo.riskLevel === 'Medium Risk'
        "
        message="Moderate Risk Score"
        description="This request has a moderate risk score. Please review carefully."
        type="info"
        show-icon
        style="margin-bottom: 16px"
      />

      <a-alert
        v-else-if="
          requestData &&
          requestData.buyerInfo &&
          requestData.buyerInfo.riskLevel === 'Low Risk'
        "
        message="Low Risk Score"
        description="This request has a low risk score. No issues detected."
        type="success"
        show-icon
        style="margin-bottom: 16px"
      />

      <a-row :gutter="[16, 16]">
        <h1
          style="
            font-size: 15px;
            font-style: bold;
            color: #000;
            margin: 14px 0px;
            text-transform: uppercase;
          "
        >
          Request Summary
        </h1>
        <a-col :span="12">
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Invoice ID:</p>
              <p class="value">{{ requestData.invoiceNumber }}</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Seller Name:</p>
              <p class="value">{{ requestData.sellerInfo.type }}</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Buyer Name:</p>
              <p class="value">{{ requestData.buyerName }}</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Invoice Amount:</p>
              <p class="value">{{ formatAmount(requestData.invoiceValue) }}</p>
            </div>
          </div>
        </a-col>
        <a-col :span="12">
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Requested Amount:</p>
              <p class="value">{{ formatAmount(requestData.requestedAmount) }}</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Fee (%):</p>
              <p class="value">{{ requestData.fee }} %</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Net Payout:</p>
              <p class="value">{{ formatAmount(calculatedNetPayout) }}</p>
            </div>
          </div>
          <div class="invoice-details">
            <div class="detail-row">
              <p class="label">Risk Score:</p>

              <span
                :style="{
                  color: riskColor,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }"
              >
                <i class="fa fa-circle"></i>
                <span class="value">{{ requestData.buyerInfo.riskLevel }}</span>
              </span>
            </div>
          </div>
        </a-col>
      </a-row>

      <a-form layout="vertical" style="margin-top: 24px">
        <a-form-item label="Disbursement Method">
          <a-select
            v-model="disbursementMethod"
            :options="[
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'mobile_money', label: 'Mobile Money' },
            ]"
            placeholder="Select method"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="Internal Note (Optional)">
          <a-textarea
            v-model="internalNote"
            rows="3"
            placeholder="Add any internal notes here..."
          />
        </a-form-item>

        <a-form-item>
          <a-switch v-model="notifySeller" />
          <span style="margin-left: 8px">Notify Seller on Approval</span>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>
<script>
import { formatAmount } from '../../utils';

export default {
  name: "InvoiceFactoringRequest",

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    requestData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      disbursementMethod: null,
      internalNote: "",
      notifySeller: true,
      internalVisible: this.visible,
    };
  },

  watch: {
    visible(val) {
      this.internalVisible = val;
    },
    internalVisible(val) {
      this.$emit("update:visible", val);
    },
  },

  computed: {
    riskColor() {
      const level = this.requestData?.buyerInfo?.riskLevel;
      switch (level) {
        case "Low Risk":
          return "green";
        case "Medium Risk":
          return "orange";
        case "High Risk":
          return "red";
        default:
          return "#000"; // fallback color
      }
    },
    calculatedNetPayout() {
      const { requestedAmount, fee } = this.requestData;
      if (!requestedAmount || !fee) return 0;
      return requestedAmount - requestedAmount * (fee / 100);
    },
  },

  methods: {
    handleOk() {
      this.$emit("ok", {
        disbursementMethod: this.disbursementMethod,
        internalNote: this.internalNote,
        notifySeller: this.notifySeller,
      });
      this.internalVisible = false;
    },
    handleCancel() {
      this.$emit("close");
    },
    formatAmount,
  },

  mounted() {
    console.log("Request Data:", this.requestData);
  },
};
</script>

<style scoped>
.invoice-details {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px 8px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.invoice-details p {
  margin: 0;
}
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.label {
  font-weight: 600;
  margin: 0;
  color: #4b5563;
}

.value {
  margin: 0;
  font-weight: 700;
  color: #333;
}
</style>
